import type {
  ChangeEvent as ReactChangeEvent,
  MouseEvent as ReactMouseEvent,
} from "react";
import {
  action,
  computed,
  makeObservable,
  observable,
  runInAction,
} from "mobx";
import { injectable } from "tsyringe";
import { ViewModel } from "@orderofchaos/mvvm";
import type { ConfirmDialogProps } from "./ConfirmDialog";
import { lockBodyScroll, unlockBodyScroll } from "@/utils/bodyScrollLock";

let dialogIdSeed = 0;

@injectable()
export class ConfirmDialogViewModel extends ViewModel<
  unknown,
  ConfirmDialogProps
> {
  private readonly uid = (dialogIdSeed += 1);

  private readonly keyDownHandler = (event: KeyboardEvent): void => {
    if (event.key === "Escape" && !this.isProcessing) {
      this.onCancel();
    }
  };

  private isKeyDownAttached = false;

  private isScrollLocked = false;

  @observable
  reasonInput = "";

  @observable
  reasonTouched = false;

  private wasOpen = false;

  constructor() {
    super();
    makeObservable(this);
  }

  @computed
  get isOpen(): boolean {
    return this.viewProps.isOpen;
  }

  @computed
  get title(): string {
    return this.viewProps.title;
  }

  @computed
  get description(): string {
    return this.viewProps.description || "";
  }

  @computed
  get hasDescription(): boolean {
    return this.description.trim().length > 0;
  }

  @computed
  get confirmLabel(): string {
    return this.viewProps.confirmLabel || "подтвердить";
  }

  @computed
  get cancelLabel(): string {
    return this.viewProps.cancelLabel || "отмена";
  }

  @computed
  get isProcessing(): boolean {
    return this.viewProps.isProcessing ?? false;
  }

  @computed
  get tone(): "danger" | "neutral" {
    return this.viewProps.tone || "danger";
  }

  @computed
  get showReasonInput(): boolean {
    return this.reasonLabel.trim().length > 0;
  }

  @computed
  get reasonLabel(): string {
    return this.viewProps.reasonPrompt || "";
  }

  @computed
  get reasonPlaceholder(): string {
    return this.viewProps.reasonPlaceholder || "Опишите причину удаления";
  }

  @computed
  get reasonRequired(): boolean {
    return this.viewProps.reasonRequired ?? false;
  }

  @computed
  get reasonMinLength(): number {
    const value = this.viewProps.reasonMinLength;
    if (typeof value !== "number" || !Number.isFinite(value)) {
      return 3;
    }

    return Math.max(1, Math.floor(value));
  }

  @computed
  get reasonMaxLength(): number {
    const value = this.viewProps.reasonMaxLength;
    if (typeof value !== "number" || !Number.isFinite(value)) {
      return 280;
    }

    return Math.max(20, Math.floor(value));
  }

  @computed
  get reasonValue(): string {
    return this.reasonInput;
  }

  @computed
  get normalizedReason(): string | null {
    const normalized = this.reasonInput.trim();
    return normalized ? normalized : null;
  }

  @computed
  get reasonError(): string {
    if (!this.showReasonInput || !this.reasonTouched) {
      return "";
    }

    const length = this.normalizedReason?.length || 0;
    if (this.reasonRequired && length < this.reasonMinLength) {
      return `Укажите причину (минимум ${this.reasonMinLength} символа)`;
    }

    return "";
  }

  @computed
  get reasonCounterLabel(): string {
    return `${this.reasonInput.length}/${this.reasonMaxLength}`;
  }

  @computed
  get canConfirm(): boolean {
    if (!this.showReasonInput) {
      return true;
    }

    if (!this.reasonRequired) {
      return true;
    }

    return (this.normalizedReason?.length || 0) >= this.reasonMinLength;
  }

  get titleId(): string {
    return `confirm-dialog-title-${this.uid}`;
  }

  get descriptionId(): string {
    return `confirm-dialog-description-${this.uid}`;
  }

  get ariaDescriptionId(): string | undefined {
    return this.hasDescription ? this.descriptionId : undefined;
  }

  protected onViewMounted(): void {
    this.syncEffects();
  }

  protected onViewUpdated(): void {
    this.syncEffects();
  }

  protected onViewUnmounted(): void {
    this.detachEscListener();
    this.unlockScroll();
  }

  @action.bound
  onOverlayMouseDown(): void {
    if (!this.isProcessing) {
      this.onCancel();
    }
  }

  @action.bound
  onDialogMouseDown(event: ReactMouseEvent<HTMLDivElement>): void {
    event.stopPropagation();
  }

  @action.bound
  onCancelClick(): void {
    if (!this.isProcessing) {
      this.onCancel();
    }
  }

  @action.bound
  onConfirmClick(): void {
    if (!this.isProcessing) {
      if (!this.canConfirm) {
        this.reasonTouched = true;
        return;
      }
      void this.viewProps.onConfirm(this.normalizedReason);
    }
  }

  @action.bound
  onReasonChange(event: ReactChangeEvent<HTMLTextAreaElement>): void {
    this.reasonInput = event.target.value;
    this.reasonTouched = true;
  }

  private get onCancel(): () => void {
    return this.viewProps.onCancel;
  }

  private syncEffects(): void {
    if (this.isOpen) {
      if (!this.wasOpen) {
        runInAction(() => {
          this.reasonInput = "";
          this.reasonTouched = false;
          this.wasOpen = true;
        });
      }
      this.attachEscListener();
      this.lockScroll();
      return;
    }

    if (this.wasOpen) {
      runInAction(() => {
        this.reasonInput = "";
        this.reasonTouched = false;
        this.wasOpen = false;
      });
    }
    this.detachEscListener();
    this.unlockScroll();
  }

  private attachEscListener(): void {
    if (typeof document === "undefined" || this.isKeyDownAttached) {
      return;
    }

    document.addEventListener("keydown", this.keyDownHandler);
    this.isKeyDownAttached = true;
  }

  private detachEscListener(): void {
    if (typeof document === "undefined" || !this.isKeyDownAttached) {
      return;
    }

    document.removeEventListener("keydown", this.keyDownHandler);
    this.isKeyDownAttached = false;
  }

  private lockScroll(): void {
    if (this.isScrollLocked) {
      return;
    }

    lockBodyScroll();
    this.isScrollLocked = true;
  }

  private unlockScroll(): void {
    if (!this.isScrollLocked) {
      return;
    }

    unlockBodyScroll();
    this.isScrollLocked = false;
  }
}
